import { Injectable, inject } from '@angular/core';
import {
  Observable,
  tap,
  finalize,
  filter,
  map,
  of,
  catchError,
  delay,
  switchMap,
  expand,
  take,
  EMPTY,
} from 'rxjs';
import {
  AssignmentStatus,
  AssignmentQuestion,
  GenerationStatus,
  GENERATION_PROGRESS_STEPS,
} from './assignment.models';
import {
  AssignmentApiService,
  GenerationStatusResponse,
} from './assignment-api.service';
import { AssignmentStateService } from './assignment-state.service';

@Injectable({
  providedIn: 'root',
})
export class AssignmentGenerationService {
  private readonly apiService = inject(AssignmentApiService);
  private readonly state = inject(AssignmentStateService);

  /** Polling interval in milliseconds */
  private readonly POLLING_INTERVAL_MS = 2000;

  // ============================================
  // STEP 1: CREATE ASSIGNMENT (Context)
  // ============================================

  /**
   * Create assignment with context data (Step 1 -> Step 2 transition)
   * Called when user clicks "Continue" on Configure step
   */
  createAssignmentContext(): Observable<void> {
    this.state.setError(null);

    const inputData = this.state.inputData;
    const existingAssignmentId = this.state.assignmentId;

    const requestData = {
      grade: String(inputData.grade!),
      tier1SubjectId: inputData.tier1SubjectId!,
      ...(inputData.tier2SubjectId && {
        tier2SubjectId: inputData.tier2SubjectId,
      }),
      ...(inputData.state && { state: inputData.state }),
      objective: inputData.objective || '',
    };

    // If assignment already exists, just update it; otherwise create new
    const operation$ = existingAssignmentId
      ? this.apiService.updateAssignment(existingAssignmentId, requestData)
      : this.apiService.createAssignment(requestData);

    return operation$.pipe(
      tap((assignment) => {
        this.state.setAssignmentId(assignment.id);
        // Set status to CONTEXT_COMPLETED to allow navigation to step 2
        this.state.setStatus(AssignmentStatus.CONTEXT_COMPLETED);
      }),
      map(() => undefined),
      catchError((err) => {
        this.state.setError(
          'Failed to create assignment. Please try again.',
        );
        throw err;
      }),
    );
  }

  // ============================================
  // STEP 2: UPDATE OBJECTIVE & GENERATE QUESTIONS
  // ============================================

  /**
   * Update objective and start question generation (Step 2 -> Step 3 transition)
   * Called when user clicks "Generate and review" on Upload step
   */
  updateObjectiveAndGenerateQuestions(): Observable<AssignmentQuestion[]> {
    this.state.setError(null);
    this.state.setIsGenerating(true);
    this.state.setGenerationProgressStep(0);
    // Set status to allow navigation to step 3
    this.state.setStatus(AssignmentStatus.OBJECTIVE_COMPLETED);

    const inputData = this.state.inputData;
    const assignmentId = this.state.assignmentId;

    if (!assignmentId) {
      this.state.setIsGenerating(false);
      return new Observable((subscriber) => {
        subscriber.error(new Error('No assignment ID'));
      });
    }

    return new Observable<AssignmentQuestion[]>((subscriber) => {
      // Step 1: Update assignment with objective and question count
      this.apiService
        .updateAssignment(assignmentId, {
          objective: inputData.objective,
          questionCount: inputData.questionCount,
        })
        .subscribe({
          next: () => {
            // Step 2: Upload files if any
            const files = this.state.uploadedFiles;
            if (files.length > 0) {
              this.state.setIsUploading(true);
              this.apiService
                .uploadResources(assignmentId, files)
                .pipe(
                  tap((progress) => {
                    this.state.setUploadProgress(progress.progress);
                  }),
                  filter((progress) => progress.completed),
                  finalize(() => this.state.setIsUploading(false)),
                )
                .subscribe({
                  next: () => {
                    // Step 3: Generate questions after upload
                    this.generateQuestionsInternal(assignmentId, subscriber);
                  },
                  error: (err) => {
                    this.state.setError(
                      'Failed to upload files. Please try again.',
                    );
                    this.state.setIsGenerating(false);
                    subscriber.error(err);
                  },
                });
            } else {
              // Step 3: Generate questions without files
              this.generateQuestionsInternal(assignmentId, subscriber);
            }
          },
          error: (err) => {
            this.state.setError(
              'Failed to update assignment. Please try again.',
            );
            this.state.setIsGenerating(false);
            subscriber.error(err);
          },
        });
    });
  }

  // ============================================
  // POLLING & GENERATION (private)
  // ============================================

  /**
   * Poll for generation status until complete or failed.
   * Uses actual backend status to determine progress phase.
   */
  private pollForGenerationStatus(
    assignmentId: string,
  ): Observable<GenerationStatusResponse> {
    let pollCount = 0;

    const makeRequest = () => {
      pollCount++;
      return this.apiService.getGenerationStatus(assignmentId).pipe(
        tap((response) => {
          let progressStep: number;
          if (
            response.generationStatus ===
            GenerationStatus.GENERATING_SCORING_GUIDE
          ) {
            // Scoring guide phase
            progressStep = 3;
          } else {
            // Question generation phase — simulate progress across first 3 steps
            progressStep = Math.min(pollCount, 2);
          }
          this.state.setGenerationProgressStep(progressStep);
        }),
      );
    };

    // Initial request, then expand with delay for subsequent requests
    return makeRequest().pipe(
      expand((response) => {
        // Stop polling if generation is complete or failed
        if (
          response.generationStatus === GenerationStatus.IDLE ||
          response.generationStatus === GenerationStatus.FAILED
        ) {
          return EMPTY;
        }
        // Wait for polling interval, then make next request
        return of(null).pipe(
          delay(this.POLLING_INTERVAL_MS),
          switchMap(() => makeRequest()),
        );
      }),
      filter(
        (response) =>
          response.generationStatus === GenerationStatus.IDLE ||
          response.generationStatus === GenerationStatus.FAILED,
      ),
      take(1),
    );
  }

  private generateQuestionsInternal(
    assignmentId: string,
    subscriber: {
      next: (value: AssignmentQuestion[]) => void;
      error: (err: any) => void;
      complete: () => void;
    },
  ): void {
    // Start async generation
    this.apiService.startQuestionGeneration(assignmentId).subscribe({
      next: () => {
        // Poll for status until complete
        this.pollForGenerationStatus(assignmentId)
          .pipe(finalize(() => this.state.setIsGenerating(false)))
          .subscribe({
            next: (statusResponse) => {
              if (
                statusResponse.generationStatus === GenerationStatus.FAILED
              ) {
                this.state.setError(
                  statusResponse.generationError ||
                    'Failed to generate questions. Please try again.',
                );
                subscriber.error(
                  new Error(
                    statusResponse.generationError || 'Generation failed',
                  ),
                );
                return;
              }

              // Fetch the updated assignment to get the questions
              this.apiService.getAssignment(assignmentId).subscribe({
                next: (assignment) => {
                  this.state.setTitle(assignment.title || '');
                  this.state.setQuestions(assignment.questions);
                  // Explicitly set status to QUESTIONS_GENERATED after successful generation
                  this.state.setStatus(AssignmentStatus.QUESTIONS_GENERATED);
                  // All progress steps complete
                  this.state.setGenerationProgressStep(
                    GENERATION_PROGRESS_STEPS.length,
                  );
                  subscriber.next(assignment.questions);
                  subscriber.complete();
                },
                error: (err) => {
                  this.state.setError(
                    'Failed to fetch assignment. Please try again.',
                  );
                  subscriber.error(err);
                },
              });
            },
            error: (err) => {
              this.state.setError(
                'Failed to check generation status. Please try again.',
              );
              subscriber.error(err);
            },
          });
      },
      error: (err) => {
        this.state.setError(
          'Failed to start question generation. Please try again.',
        );
        this.state.setIsGenerating(false);
        subscriber.error(err);
      },
    });
  }
}
